/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { authService } from '@/services/authService'
import { LoginRequest, Role } from '@/types/auth'
import { toast } from 'sonner'
import { useAuth } from '@/stores/authStore'

export function useLogin() {
  const { setUser, setTokens, setAccountType } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await authService.login(credentials)
      if (response.access_token) {
        const { access_token, refresh_token, user } = response

        if (
          !user ||
          (!user.role.includes(Role.TUTOR) && !user.role.includes(Role.ADMIN))
        ) {
          toast.error('Unauthorized access', {
            duration: 5000,
            position: 'top-right',
          })
          // setError('Only tutor role is allowed')
          return false
        }

        setUser(user)
        setTokens(access_token, refresh_token)
        setAccountType(credentials.created_type)
        return true
      } else {
        setError(`${(response as any)?.detail}`)
        return false
      }
    } catch (_err) {
      setError('An unexpected error occurred. Please try again.')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  return {
    login,
    isLoading,
    error,
  }
}

export function useLoginLMS() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await authService.login(credentials)
      if (response.authorization_url) {
        window.open(response.authorization_url, '_blank')
        return false
      }
    } catch (_err) {
      setError('An unexpected error occurred. Please try again.')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  return {
    login,
    isLoading,
    error,
  }
}

export function useExchangeToken() {
  const { setUser, setTokens } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const exchangeToken = async (code: string) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await authService.exchangeToken({ code })
      if (response.access_token) {
        const { access_token, refresh_token, user } = response
        if (!user || (user.role && !user.role.includes(Role.TUTOR))) {
          toast.error('Unauthorized access', {
            duration: 5000,
            position: 'top-right',
          })
          return false
        }
        setUser(user)
        setTokens(access_token, refresh_token)
        return true
      }
      return false
    } catch (err: any) {
      setError(err.message || 'Failed to exchange token')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  return {
    exchangeToken,
    isLoading,
    error,
  }
}

export function useLogout() {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)

  const logout = async () => {
    try {
      setIsLoading(true)

      await authService.logout()
      navigate({
        to: '/sign-in',
        search: { redirect: undefined },
        replace: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return {
    logout,
    isLoading,
  }
}

export function useAutoRestoreUser() {
  const { accessToken, user, restoreUser } = useAuth()
  const [isRestoring, setIsRestoring] = useState(false)

  useEffect(() => {
    const checkAndRestoreUser = async () => {
      // Only restore if we have accessToken but no user
      if (accessToken && !user && !isRestoring) {
        setIsRestoring(true)
        try {
          await restoreUser()
        } catch (_error) {
          // Auto restore user failed silently
        } finally {
          setIsRestoring(false)
        }
      }
    }

    checkAndRestoreUser()
  }, [accessToken, user, restoreUser, isRestoring])

  return {
    isRestoring,
  }
}
